/**
 * WorkshopAccessGate
 *
 * Email-confirmation gate that wraps the protected content on
 * /claude-workshop-replay.
 *
 * Repeat visits are unlocked from a short-lived, ed25519-signed access token
 * (minted by the verify endpoint, verified in-browser with the public key), so
 * a returning buyer reloading the page does ZERO network and ZERO Stripe work
 * for ~24h. Only the cold path (no token yet, or an expired one) hits the
 * endpoint. Shares the token util with AccessGate; scope is pinned to
 * "workshop" so a masterclass token can never unlock this page.
 *
 * Three states (discriminated union):
 *   - loading: a verify request is in flight (mount-time revalidation, or
 *     the user just submitted the form)
 *   - locked: show the email-input form
 *   - unlocked: render children (the gated content)
 *
 * Mount order:
 *   (a) valid "workshop" token → unlock immediately, no fetch, no Stripe.
 *   (b) else cached email → one background re-verify to mint a fresh token.
 *   (c) else → locked form.
 * On a successful submit, the returned token AND the email are stored.
 *
 * Fail-open-to-old-behavior: if VITE_ED25519_PUBLIC_KEY_HEX is unset or a token
 * fails to verify, we fall back to the cached-email server re-verify (b) — the
 * previous verify-every-visit behavior — never a silent unlock.
 *
 * NOTE: localStorage stores an email plus a signed token. The token is bound to
 * its scope and expiry, so it cannot be forged or replayed across gates. A
 * tampered EMAIL either names a real buyer (already known to the tamperer, so
 * read access is moot for a YouTube replay) or a non-buyer (the live Stripe
 * check denies). The enumeration risk via repeated POSTs is an accepted
 * trade-off — see the paywall design doc (prompts/089).
 */

import { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { trackEvent } from "@/utils/posthog";
import { useHydratedSearch } from "@/hooks/useHydratedSearch";
import { buildOrganizationJourneyUrl } from "@/utils/organizationJourney";
import {
  clearStoredToken,
  readStoredToken,
  storeToken,
  verifyAccessToken,
} from "@/lib/accessToken";

const STORAGE_KEY = "workshop_email";
const ACCESS_SCOPE = "workshop" as const;
const SUPPORT_EMAIL = "support@technical-leaders.com";
const VERIFY_ENDPOINT = "/api/stripe/workshop-verify-email";

type GateState =
  | { kind: "loading" }
  | { kind: "locked"; error?: string }
  | { kind: "unlocked"; email: string };

interface WorkshopAccessGateProps {
  children: React.ReactNode;
}

async function verifyEmail(
  email: string,
): Promise<{ ok: boolean; token: string | null }> {
  try {
    const resp = await fetch(VERIFY_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });
    if (!resp.ok) return { ok: false, token: null };
    const data = (await resp.json()) as { access?: boolean; token?: string };
    if (data.access !== true) return { ok: false, token: null };
    return { ok: true, token: typeof data.token === "string" ? data.token : null };
  } catch {
    return { ok: false, token: null };
  }
}

function readCachedEmail(): string | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const normalized = raw.trim().toLowerCase();
    return normalized.includes("@") ? normalized : null;
  } catch {
    return null;
  }
}

function clearCachedEmail() {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore (Safari private mode, etc.)
  }
}

function cacheEmail(email: string) {
  try {
    window.localStorage.setItem(STORAGE_KEY, email);
  } catch {
    // ignore (Safari private mode, etc.)
  }
}

const WorkshopAccessGate = ({ children }: WorkshopAccessGateProps) => {
  const teamOfferHref = buildOrganizationJourneyUrl("/ai-30", useHydratedSearch(), {
    source: "workshop-access",
    location: "team_program",
    audience: "organization",
  });
  // Initialize from localStorage synchronously so the very first paint is
  // either `loading` (a stored token or cached email exists — resolved in the
  // effect) or `locked` (fresh visitor) — never a flash of the unlocked UI
  // before verification finishes.
  const [state, setState] = useState<GateState>(() => {
    if (typeof window === "undefined") return { kind: "loading" };
    const hasCredential = !!readStoredToken(STORAGE_KEY) || !!readCachedEmail();
    return hasCredential ? { kind: "loading" } : { kind: "locked" };
  });
  const [emailInput, setEmailInput] = useState("");
  const lockedShownRef = useRef(false);

  // Mount-time unlock. Order: (a) valid token → instant unlock, no fetch;
  // (b) cached email → one background re-verify to mint a fresh token;
  // (c) neither → locked form.
  useEffect(() => {
    if (state.kind !== "loading") return;

    let cancelled = false;
    (async () => {
      // (a) Token-first: verify signature + expiry + scope entirely in-browser.
      const storedToken = readStoredToken(STORAGE_KEY);
      const claims = await verifyAccessToken(storedToken, ACCESS_SCOPE);
      if (cancelled) return;
      if (claims) {
        setState({ kind: "unlocked", email: claims.email });
        trackEvent("Workshop Replay Access Granted", { source: "token" });
        return;
      }

      // (b) Cached email → single Stripe-backed re-verify to refresh the token.
      const cached = readCachedEmail();
      if (!cached) {
        clearStoredToken(STORAGE_KEY);
        setState({ kind: "locked" });
        return;
      }

      const { ok, token } = await verifyEmail(cached);
      if (cancelled) return;
      if (ok) {
        if (token) storeToken(STORAGE_KEY, token);
        setState({ kind: "unlocked", email: cached });
        trackEvent("Workshop Replay Access Granted", { source: "cached" });
      } else {
        clearCachedEmail();
        clearStoredToken(STORAGE_KEY);
        setState({ kind: "locked" });
      }
    })();

    return () => {
      cancelled = true;
    };
    // Run only on mount — state transitions are driven by user actions
    // after this; we don't want to retrigger the cached-credential path.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fire the "gate shown" event the first time the locked state is rendered.
  useEffect(() => {
    if (state.kind === "locked" && !lockedShownRef.current) {
      lockedShownRef.current = true;
      trackEvent("Workshop Replay Gate Shown");
    }
  }, [state.kind]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const candidate = emailInput.trim().toLowerCase();
    if (!candidate || !candidate.includes("@")) {
      setState({
        kind: "locked",
        error: "Please enter a valid email address.",
      });
      return;
    }

    setState({ kind: "loading" });
    const { ok, token } = await verifyEmail(candidate);
    if (ok) {
      cacheEmail(candidate);
      if (token) storeToken(STORAGE_KEY, token);
      setState({ kind: "unlocked", email: candidate });
      trackEvent("Workshop Replay Access Granted", { source: "submit" });
    } else {
      clearCachedEmail();
      clearStoredToken(STORAGE_KEY);
      setState({
        kind: "locked",
        error:
          "We couldn't find a purchase for that email. Try the one you used at Stripe checkout, or contact support.",
      });
      trackEvent("Workshop Replay Access Denied");
    }
  };

  const handleSignOut = () => {
    clearCachedEmail();
    clearStoredToken(STORAGE_KEY);
    trackEvent("Workshop Replay Sign Out");
    if (typeof window !== "undefined") {
      window.location.reload();
    }
  };

  if (state.kind === "loading") {
    return (
      <div className="w-full max-w-5xl mx-auto">
        <div className="flex flex-col items-center justify-center py-24 text-muted-foreground">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="mt-4 text-sm">Verifying your access&hellip;</p>
        </div>
      </div>
    );
  }

  if (state.kind === "locked") {
    return (
      <div className="w-full max-w-xl mx-auto">
        <Card className="border-primary/20 bg-background/80 backdrop-blur shadow-xl">
          <CardHeader>
            <CardTitle className="text-2xl text-center">
              Confirm your email to watch the replay
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground text-center mb-6">
              Enter the email address you used when you purchased.
            </p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                type="email"
                autoComplete="email"
                required
                placeholder="you@example.com"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                className="h-12 text-base"
              />
              <Button
                type="submit"
                size="lg"
                className="w-full text-base"
                disabled={!emailInput.trim()}
              >
                Unlock the workshop
              </Button>
            </form>
            {state.error && (
              <p
                role="alert"
                className="mt-4 text-sm text-destructive text-center"
              >
                {state.error.includes("contact support") ? (
                  <>
                    We couldn&apos;t find a purchase for that email. Try the one
                    you used at Stripe checkout, or{" "}
                    <a
                      href={`mailto:${SUPPORT_EMAIL}`}
                      className="underline hover:no-underline"
                    >
                      contact support
                    </a>
                    .
                  </>
                ) : (
                  state.error
                )}
              </p>
            )}
            <p className="mt-6 text-xs text-muted-foreground text-center">
              Bring your team together for hands-on AI training.{" "}
              <a
                href={teamOfferHref}
                className="underline hover:no-underline"
              >
                Explore AI 30 for your team
              </a>
              .
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="mx-auto mb-6 flex max-w-5xl items-center justify-center gap-2 text-xs text-muted-foreground">
        <span>
          Watching as <span className="font-medium">{state.email}</span>
        </span>
        <span aria-hidden>&middot;</span>
        <button
          type="button"
          onClick={handleSignOut}
          className="underline hover:no-underline"
        >
          sign out
        </button>
      </div>
      {children}
    </div>
  );
};

export default WorkshopAccessGate;
